import {h, isValidElement} from 'preact'
import * as preact from 'preact/compat'
import {useState, useEffect, useMemo} from 'preact/compat'
import clsx from 'clsx'
import css from 'minify-css.macro'
import {Column} from '@dash-ui/react-layout'
import {styles} from '../styles'
import {useDependency} from '../hooks'
import {Notice} from './Notice'
import {Spinner} from './Spinner'

export const ReactPreview: React.FC<ReactPreviewProps> = ({code, scope}) => {
  const [error, setError] = useState<Error>(null)
  const [{transform}, buble] = useDependency('buble')
  const element = useMemo(
    () => {
      if (buble.status === 'success') {
        setError(null)
        return createElementTransform(
          (code) => transform(code, opts).code,
          {code, scope},
          (error) => setError(error)
        )
      } else if (buble.status === 'error') {
        setError(buble.error)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, buble.status]
  )

  useEffect(() => {
    error && console.warn(error)
  }, [error])

  return (
    <div className={clsx(styles.theme('sandbox'), preview())}>
      {buble.status === 'loading' && (
        <Column align='center' distribute='center' height='100%'>
          <Spinner size={32} />
        </Column>
      )}
      {!error && element}
      {error && (
        <div className={previewError()}>
          <Notice variant='danger'>
            <Column gap='md'>
              <b>
                {error.toString().match(/Error:/g)
                  ? error.toString().split('Error:')[0] + 'Error'
                  : 'Error:'}
              </b>
              <pre style={{overflow: 'auto'}}>
                {error.toString().split('Error:')[1]}
              </pre>
            </Column>
          </Notice>
        </div>
      )}
    </div>
  )
}

const preview = styles.one(
  ({color}) => css`
    width: 100%;
    height: 100%;
    min-height: 320px;
    overflow: auto;
    background: ${color['ui/primary']};
  `
)

const previewError = styles.one(
  ({pad}) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: ${pad.md};
    margin: 0 auto;
    overflow: auto;
  `
)

export interface ReactPreviewProps {
  code: string
  scope: Record<string, any>
}

export const _poly = {assign: Object.assign}

const opts = {
  objectAssign: '_poly.assign',
  transforms: {
    dangerousForOf: true,
    dangerousTaggedTemplateString: true,
  },
}

export const createElementTransform = (
  transform: (code: string) => string,
  {code = '', scope = {}},
  setError: (error: Error | null) => void
) => {
  const withErrorBoundary = (
    Component: () => React.ReactElement | React.ComponentType
  ) => {
    return class ErrorBoundary extends preact.Component {
      componentDidCatch(error: Error) {
        setError(error)
      }

      render() {
        let element: JSX.Element | React.ComponentType = null

        try {
          element = Component()

          if (typeof element === 'function') {
            element = h(element, {})
          } else if (!isValidElement(element)) {
            throw new SyntaxError(
              `'Component' was not a React element or component.`
            )
          }
        } catch (error) {
          setError(error)
          element = null
        }

        return element
      }
    }
  }

  try {
    code = `
      ${code.trim().replace(/;$/, '')};
      return Component;
    `.trim()
    const res = new Function(
      '_poly',
      'React',
      ...Object.keys(scope),
      transform(code)
    )

    return h(
      withErrorBoundary(() => res(_poly, preact, ...Object.values(scope))),
      {}
    )
  } catch (error) {
    setError(error)
    return null
  }
}
